import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import {
  ACCOUNT_NAME_MAX,
  accountLabel,
  awsAccountDisplayName,
  marketplaceAccountDisplayName,
  normalizeAccountName,
} from '../../../shared/account-names.mjs';

/**
 * Friendly names for the customer's AWS account and its AWS Marketplace
 * subscription account (ENTRAID-294), on the Deployment page identity card.
 * Editable by operators and by the customer admin (the API enforces it).
 *
 * Reading: "Name (id)" per the display rule in shared/account-names.mjs (the
 * IAM alias when present, else the stored name, else nothing; the id is always
 * shown). Editing: the two stored names only. The alias comes from the AWS
 * account itself and is not editable here.
 *
 * `awsAccountId` and `marketplaceAccountId` are the 12-digit ids from
 * shared/customer-account.mjs; the Marketplace row is left out when the
 * record has no such account.
 */
export default function AccountNamesField({ customer, awsAccountId, marketplaceAccountId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [awsName, setAwsName] = useState(customer.awsAccountName ?? '');
  const [marketplaceName, setMarketplaceName] = useState(customer.marketplaceAccountName ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!editing) {
      setAwsName(customer.awsAccountName ?? '');
      setMarketplaceName(customer.marketplaceAccountName ?? '');
    }
  }, [customer.awsAccountName, customer.marketplaceAccountName, editing]);

  const sameAccount = Boolean(awsAccountId) && awsAccountId === marketplaceAccountId;
  const alias = typeof customer.awsAccountAlias === 'string' && customer.awsAccountAlias.trim() ? customer.awsAccountAlias.trim() : null;

  async function handleSave() {
    const aws = normalizeAccountName(awsName);
    const mp = normalizeAccountName(marketplaceName);
    if (!aws.ok) { setError(`AWS account name ${aws.error}.`); return; }
    if (!mp.ok) { setError(`Marketplace account name ${mp.error}.`); return; }
    setBusy(true); setError(null);
    try {
      const body = { awsAccountName: aws.value };
      if (marketplaceAccountId) body.marketplaceAccountName = mp.value;
      await api.updateCustomer(customer.customerId, body);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function handleCancel() {
    setAwsName(customer.awsAccountName ?? '');
    setMarketplaceName(customer.marketplaceAccountName ?? '');
    setError(null);
    setEditing(false);
  }

  if (!editing) {
    return (
      <div className="flex flex-col gap-1" data-testid="account-names">
        <span className="font-mono break-all" data-testid="account-names-aws">
          {accountLabel(awsAccountDisplayName(customer), awsAccountId)}
        </span>
        {marketplaceAccountId && !sameAccount && (
          <span className="font-mono break-all" data-testid="account-names-marketplace">
            <span className="font-sans text-xs text-slate-500">Marketplace: </span>
            {accountLabel(marketplaceAccountDisplayName(customer), marketplaceAccountId)}
          </span>
        )}
        <span>
          <button type="button" className="text-xs text-ipc-light hover:underline" onClick={() => setEditing(true)}>
            Edit names
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="account-names-form">
      <label className="text-xs text-slate-600" htmlFor="aws-account-name">
        AWS account name{awsAccountId ? ` (${awsAccountId})` : ''}
      </label>
      <input
        id="aws-account-name"
        className="input text-sm py-1"
        value={awsName}
        onChange={(e) => setAwsName(e.target.value)}
        maxLength={ACCOUNT_NAME_MAX}
        disabled={busy}
      />
      {alias && (
        <p className="text-xs text-slate-500">
          The account's own alias ({alias}) is shown instead of this name while it is set.
        </p>
      )}
      {marketplaceAccountId && (
        <>
          <label className="text-xs text-slate-600" htmlFor="marketplace-account-name">
            Marketplace subscription account name ({marketplaceAccountId})
          </label>
          <input
            id="marketplace-account-name"
            className="input text-sm py-1"
            value={marketplaceName}
            onChange={(e) => setMarketplaceName(e.target.value)}
            maxLength={ACCOUNT_NAME_MAX}
            disabled={busy}
          />
        </>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="text-xs px-2 py-1 rounded bg-ipc-light text-white hover:bg-ipc-primary transition-colors disabled:opacity-50"
          onClick={handleSave}
          disabled={busy}
        >
          {busy ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
          onClick={handleCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
      {error && <span role="alert" className="text-xs text-red-700">{error}</span>}
    </div>
  );
}
